import { ButtonCus } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Label } from '@/components/common/Label';
import { Textarea } from '@/components/common/Textarea';
import ImageUpload from '@/components/general/ImageUpload';
import { Logo } from '@/components/general/logo/Logo';
import SelectLanguageBtn from '@/components/general/select-language/SelectLanguageBtn';
import { CHECK_BIKE_BEFORE_RETURN_EK, CHECK_BIKE_BEFORE_RETURN_GAS, DEFAULT_LANGUAGE, ekDomain } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { isPathEk } from '@/utils/routes';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

export default observer(function BikeReturnPage() {
    // hook
    const { t, i18n } = useTranslation();
    const { orderId } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const isEk = isPathEk(pathname);
    const CHECK_BIKE_BEFORE_RETURN = isEk ? CHECK_BIKE_BEFORE_RETURN_EK : CHECK_BIKE_BEFORE_RETURN_GAS;

    // store
    const {
        bikeReturnStore: {
            getBeforeBikeReturn,
            postBikeReturn,
            clean
        }, modalStore: { showAlertModal }
    } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        returnOptions: yup.array().of(yup.number()).optional(),
        parkedVehiclePosition: yup
            .number()
            .required('form_error_validate.required'),
        bikeDamagePhotos: yup.array().of(yup.number()).optional(),
        note: yup.string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            returnOptions: [],
            parkedVehiclePosition: undefined,
            bikeDamagePhotos: [],
            note: ''
        }
    });

    // lifecycle
    useEffect(() => {
        onFetch(orderId);

        return () => {
            clean();
        };
    }, [orderId]);

    useEffect(() => {
        return () => {
            setTimeout(() => {
                i18n.changeLanguage(DEFAULT_LANGUAGE).catch(console.error);
            }, 100);
        };
    }, [i18n]);

    // function
    const onFetch = async (id: string | undefined) => {
        if (id) {
            let result = await flowResult(getBeforeBikeReturn(Number(id)));
            if (result) {
                if (result?.returnDate) {
                    showAlertModal({
                        id: 'return-success',
                        type: 'error',
                        content: t('messages.completed_return_bike'),
                        onClickXIcon: () => {
                            navigate(
                                isEk ?
                                    `/${ekDomain}/my-account/rental-history/${orderId}` :
                                    `/my-account/rental-history/${orderId}`
                            );
                        }
                    });
                }
            }
        }
    };

    const onSubmit = async (data: FormData) => {
        const { returnOptions, parkedVehiclePosition, bikeDamagePhotos, note } =
            data;

        let returnOptionsConvert: number[] = [];
        let bikeDamagePhotosConvert: number[] = [];

        if (Array.isArray(returnOptions) && returnOptions?.length > 0) {
            returnOptionsConvert = returnOptions.filter(
                (item): item is number => item !== undefined && item !== null
            );
        }
        if (Array.isArray(bikeDamagePhotos) && bikeDamagePhotos?.length > 0) {
            bikeDamagePhotosConvert = bikeDamagePhotos.filter(
                (item): item is number => item !== undefined && item !== null
            );
        }

        const dataSubmit = {
            returnOptions: returnOptionsConvert,
            parkedVehiclePosition: parkedVehiclePosition || null,
            bikeDamagePhotos: bikeDamagePhotosConvert,
            note: note || ''
        };

        const res = await flowResult(
            postBikeReturn({ ...dataSubmit, id: Number(orderId) })
        );
        if (res) {
            showAlertModal({
                id: 'return-success',
                type: 'success',
                content: t('messages.return_success'),
                onClickXIcon: () => {
                    navigate(
                        isEk ?
                            `/${ekDomain}/my-account/rental-history/${orderId}` :
                            `/my-account/rental-history/${orderId}`
                    );
                }
            });
        }
    };

    return (
        <div>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <div className='box-item relative'>
                        <div className='grid grid-cols-1 lg:grid-cols-3 gap-[min(24px,5vw)] lg:gap-3 justify-between items-center pb-[min(32px,6vw)]'>
                            <div className='hidden lg:block'></div>
                            <div className='flex justify-center'>
                                <div className='logo w-[min(240px,55vw)]'>
                                    <Logo />
                                </div>
                            </div>
                            <div className='flex justify-center lg:justify-end'>
                                <SelectLanguageBtn />
                            </div>
                        </div>
                        <div className='text-[min(24px,calc(100vw/17))] xsm:text-fs-mid24 leading-lh-mid24 font-semibold text-center whitespace-normal pb-[min(40px,6vw)]'>
                            {t(`return_bike_form.title.${isEk ? 'title_ek' : 'title_gas'}`)}
                        </div>

                        <div className='text-left pb-[min(40px,6vw)]'>
                            {t('return_bike_form.description.des_1')}
                            <br />
                            {isEk ?
                                (
                                    <>
                                        {t('return_bike_form.description.des_2_ek')}
                                        <br className='block xsm:hidden' />
                                        {t('return_bike_form.description.des_3_ek')}
                                    </>
                                ) :
                                (
                                    <>
                                        {t('return_bike_form.description.des_2_gas')}
                                        <br className='block sm:hidden' />
                                        {t('return_bike_form.description.des_3_gas')}
                                    </>
                                )}
                        </div>

                        <div className='grid gap-[min(40px,6vw)]'>
                            <FormField
                                control={form.control}
                                name='returnOptions'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel>{t('return_bike_form.check_box.title')}</FormLabel>
                                        <div className='grid gap-1.5 xsm:gap-2 ml-3'>
                                            {Object.values(CHECK_BIKE_BEFORE_RETURN).map(
                                                option => (
                                                    <div
                                                        key={option?.value}
                                                        className='flex items-center'
                                                    >
                                                        <FormControl>
                                                            <Checkbox
                                                                checked={
                                                                    field?.value?.includes(option.value) || false
                                                                }
                                                                onCheckedChange={(checked) => {
                                                                    if (checked) {
                                                                        field.onChange([
                                                                            ...(field?.value || []),
                                                                            option.value
                                                                        ]);
                                                                    } else {
                                                                        field.onChange(
                                                                            (field?.value || []).filter(
                                                                                item => item !== option.value
                                                                            )
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                        </FormControl>
                                                        <Label className='ml-1.5 mb-0 cursor-pointer font-normal'>
                                                            {t(option.name)}
                                                        </Label>
                                                    </div>
                                                )
                                            )}
                                        </div>
                                    </FormItem>
                                )}
                            />

                            <div>
                                <FormLabel required className='mb-0'>
                                    {t(`return_bike_form.field.${isEk ? 'bike_pictures_ek' : 'bike_pictures_gas'}`)}
                                </FormLabel>
                                <p className='mb-2'>
                                    駐車完了の状態で横向きの写真を撮影して下さい。
                                    <br />
                                    設置場所が分かりやすいよう、
                                    <br className='block sm:hidden' />
                                    可能な場合は5〜10mほど離れて撮影して下さい。
                                </p>
                                <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'>
                                    <ImageUpload
                                        filedName='parkedVehiclePosition'
                                        form={form}
                                        required={true}
                                    />
                                </div>
                            </div>

                            <div>
                                <FormLabel className='mb-0'>
                                    {t(`return_bike_form.field.${isEk ? 'image_scratches_falls_bike_ek' : 'image_scratches_falls_bike_gas'}`)}
                                </FormLabel>
                                <p className='mb-2'>
                                    {t(`return_bike_form.sentences.${isEk ? 'note_scratches_falls_bike_1_ek' : 'note_scratches_falls_bike_1_gas'}`)}
                                    <br className='block sm:hidden' />
                                    {t(`return_bike_form.sentences.${isEk ? 'note_scratches_falls_bike_2_ek' : 'note_scratches_falls_bike_2_gas'}`)}
                                    <br />
                                    <span className='text-[#737C7C]'>
                                        {t('return_bike_form.sentences.note_scratches_falls_bike_3')}
                                    </span>
                                </p>
                                <div className='grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6'>
                                    {Array.from({ length: 3 }).map((_, index) => (
                                        <ImageUpload
                                            key={index}
                                            filedName={`bikeDamagePhotos[${index}]`}
                                            form={form}
                                            required={false}
                                        />
                                    ))}
                                </div>
                            </div>

                            <FormField
                                control={form.control}
                                name='note'
                                render={({ field }) => (
                                    <FormItem className='col-span-full'>
                                        <FormLabel>
                                            {t('return_bike_form.field.comments')}
                                        </FormLabel>
                                        <FormControl>
                                            <Textarea
                                                placeholder={t('placeholder.enter')}
                                                className='min-h-32'
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage className='mt-1' />
                                    </FormItem>
                                )}
                            />

                            <p className='text-center text-fs-mid13 xsm:text-fs-mid14'>
                                {isEk ? t('return_bike_form.sentences.note_ek') : t('return_bike_form.sentences.note_gas')}
                            </p>
                        </div>
                    </div>

                    <div className='flex justify-center mt-[min(40px,8vw)]'>
                        <ButtonCus type='submit' variant='default' size='md'>
                            {t('button.send')}
                        </ButtonCus>
                    </div>
                </form>
            </Form>
        </div>
    );
});
